import { Col, Flex, Form, Typography } from "antd";
import { FORM_VALIDATION_DEBOUNCE } from "constants/form";
import {
  ExpandNetworkType,
  NETWORK_TEXT_WITHOUT_STANDARD,
  NetworkType,
  NOTIFICATION_TYPE_ENUM,
  REQUEST_CHECK_STATUS_ENUM,
  RISK_LEVEL_ENUM,
  WalletEntity,
  WalletRequest,
  WalletRiskLevel,
} from "interfaces";
import { observer } from "mobx-react-lite";
import { useNotificationContext } from "providers";
import { useEffect, useState } from "react";
// import { editProviderWallets } from "services/settingsService";
import { useStores } from "stores";
import { DefaultInput, CustomButton } from "ui";
import { getWalletRiskMessage, parseErrorMessage, useWalletRisk } from "utils";
import { _isRequired, _isValidNetworkAddress } from "utils/form/simple-validation";
import { AmlAlert } from "components/AmlAlert";
import { CHANGE_WALLET_ADDRESS_MESSAGE } from "constants/network";

interface IProps {
  currentWallet?: WalletEntity;
  network: NetworkType;
}

export const EditWalletModal = observer((props: IProps) => {
  const { currentWallet, network } = props;

  const { actionModals, settings, user } = useStores();
  const { openNotification } = useNotificationContext();

  const companyId = user.userInfo?.company_id;
  const wallets = settings.providerWallets;
  const walletNetwork = (currentWallet?.network as ExpandNetworkType) ?? network;

  const [loading, setLoading] = useState(false);

  const [form] = Form.useForm();

  const [isWalletChecked, setIsWalletChecked] = useState(false);
  const [canCheckWallet, setCanCheckWallet] = useState(false);
  const [checkedWallet, setCheckedWallet] = useState<string | null>(null);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [savedRiskLevel, setSavedRiskLevel] = useState<WalletRiskLevel>();

  const { riskLevel, riskStatus } = useWalletRisk(
    {
      walletAddress: form.getFieldsValue().wallet ?? "",
      userId: user.userInfo?.id,
    },
    canCheckWallet
  );

  useEffect(() => {
    if (riskLevel && riskStatus) {
      setSavedRiskLevel(riskLevel);
      setIsWalletChecked(true);
      setCheckedWallet(form.getFieldsValue().wallet ?? "");
      setCanCheckWallet(false);
      if (riskStatus === REQUEST_CHECK_STATUS_ENUM.SUCCESS) {
        editWallet();
      }
      if (riskStatus === REQUEST_CHECK_STATUS_ENUM.APPROVE_NEEDED) {
        setLoading(false);
        if (![RISK_LEVEL_ENUM.MEDIUM].includes(riskLevel)) {
          setButtonDisabled(true);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [riskLevel]);

  const editWallet = async () => {
    if (!companyId) return;

    let dataToSend: WalletRequest[] = [];

    if (currentWallet) {
      const updatedWallets = wallets.filter((wallet) => wallet.id !== currentWallet.id);

      dataToSend = [
        ...updatedWallets,
        {
          network: walletNetwork as ExpandNetworkType,
          address: form.getFieldsValue().wallet,
        },
      ];
    } else {
      const otherNetworkWallets = wallets.filter((wallet) => wallet.network !== walletNetwork);

      dataToSend = [
        ...otherNetworkWallets,
        {
          network: walletNetwork as ExpandNetworkType,
          address: form.getFieldsValue().wallet,
        },
      ];
    }

    // await editProviderWallets({ id: companyId }, dataToSend)
    //   .then(() => {
    //     openNotification({
    //       message: "Данные успешно обновлены",
    //       type: NOTIFICATION_TYPE_ENUM.SUCCESS,
    //     });
    //     setTimeout(() => settings.fetchWallets(companyId), 1500);
    //     handleCloseModal();
    //   })
    //   .catch((e) => {
    //     openNotification({
    //       description: parseErrorMessage(e),
    //       type: NOTIFICATION_TYPE_ENUM.ERROR,
    //     });
    //   })
    //   .finally(() => {
    //     setLoading(false);
    //   });
  };

  const editWalletHandler = async () => {
    setLoading(true);

    if (isWalletChecked && checkedWallet === form.getFieldsValue().wallet) {
      editWallet();
    } else {
      setIsWalletChecked(false);
      setCanCheckWallet(true);
    }
  };

  const handleCloseModal = () => {
    actionModals.closeAll();
  };

  const getSubmitButtonTooltip = () => {
    if (buttonDisabled) {
      return CHANGE_WALLET_ADDRESS_MESSAGE;
    }
    return undefined;
  };

  return (
    <Flex vertical>
      <Form
        form={form}
        name="editWallet"
        initialValues={{
          wallet: currentWallet?.address ?? "",
        }}
        onFinish={() => editWalletHandler()}
      >
        <Flex vertical>
          <>
            <Form.Item
              name="wallet"
              rules={[_isRequired("Поле обязательно для ввода"), _isValidNetworkAddress(walletNetwork)]}
              validateDebounce={FORM_VALIDATION_DEBOUNCE}
            >
              <DefaultInput
                placeholder={`Адрес в сети ${NETWORK_TEXT_WITHOUT_STANDARD[walletNetwork]}`}
                disabled={loading}
                onChange={(e) => {
                  if (buttonDisabled) {
                    setButtonDisabled(false);
                    setIsWalletChecked(false);
                  }
                }}
              />
            </Form.Item>
            {isWalletChecked && !loading && savedRiskLevel !== RISK_LEVEL_ENUM.LOW && savedRiskLevel && (
              <Form.Item>
                <AmlAlert amlMessage={getWalletRiskMessage(savedRiskLevel)} />
              </Form.Item>
            )}

            <Form.Item>
              <Flex gap="large">
                <Col>
                  <CustomButton
                    htmlType="submit"
                    loading={loading}
                    disabled={loading || buttonDisabled}
                    tooltip={getSubmitButtonTooltip()}
                  >
                    Сохранить
                  </CustomButton>
                </Col>
                <Col>
                  <CustomButton variant="outlined" loading={loading} disabled={loading} onClick={handleCloseModal}>
                    Отмена
                  </CustomButton>
                </Col>
              </Flex>
              {!isWalletChecked && loading && (
                <Col className="margin-top-xs">
                  <Typography.Text className="text-description">Идет проверка кошелька</Typography.Text>
                </Col>
              )}
              {riskStatus === REQUEST_CHECK_STATUS_ENUM.SUCCESS && (
                <Col className="margin-top-xs">
                  <Typography.Text className="text-description">Проверка кошелька пройдена</Typography.Text>
                </Col>
              )}
            </Form.Item>
          </>
        </Flex>
      </Form>
    </Flex>
  );
});
